import React from 'react';
import User from '~/models/User';
import {
    CheckCircleIcon,
    AlertTriangleIcon,
    NotStartedIcon,
    RefreshIcon,
    XCircleIcon
} from '@sumologic/yama-ds-icons';
import ActionResultModel from '../../../models/ActionResult';
import PropTypes from 'prop-types';
import { formatAsUserShortDateTime } from '~/modules/helpers/date';
import { stripHtml } from '~/modules/helpers';
import { Button } from '@sumologic/yama-ds-components';
import Collapse from '~/containers/Collapse/Collapse';
import Icon from '../../../components/Icon/Icon';
import Translate from '../../../components/Translate/Translate';
import { checkFeatureFlag } from '../../../modules/helpers';

//RN: SHARED FUNCTIONS - ActionResult.jsx && SliderActionResult.jsx

const getStatusColor = status => {
    let statusClasses = 'var(--bs-app-warning)';
    if (status === 'success') {
        statusClasses = 'var(--bs-app-success)';
    } else if (status === 'running') {
        statusClasses = 'var(--bs-app-warning)';
    } else if (status === 'failed' || status === 'failure') {
        statusClasses = 'var(--bs-app-danger)';
    } else if (status === 'waiting') {
        statusClasses = 'var(--bs-app-warning)';
    } else if (status === 'warning') {
        statusClasses = 'var(--bs-app-warning)';
    } else if (
        status === 'killed' ||
        status === 'Not Executed: Limit Exceeded' ||
        status === 'Not Executed: Loop Detected'
    ) {
        statusClasses = 'var(--bs-app-danger)';
    }
    return statusClasses;
};

const getStatusIcon = status => {
    const color = getStatusColor(status);
    const iconProps = {
        fill: color,
        'data-node-status': status
    };
    if (status === 'running') {
        return <RefreshIcon className="spinning" {...iconProps} />;
    } else if (status === 'failed' || status === 'failure') {
        return <XCircleIcon {...iconProps} />;
    } else if (status === 'waiting') {
        return <NotStartedIcon {...iconProps} />;
    } else if (status === 'success') {
        return <CheckCircleIcon {...iconProps} />;
    } else if (status === 'warning') {
        return <AlertTriangleIcon {...iconProps} />;
    } else if (status === 'killed') {
        return <XCircleIcon {...iconProps} />;
    } else {
        return null;
    }
};

const getUserDetails = (data, setUsername) => {
    return null;
    /* TODO: this part need to be revised
    if (data?.user_id && checkFeatureFlag('full_csoar'))
        User.getDetails(data.user_id)
            .then(response => {
                setUsername(response.data.data.username);
            })
            .catch(exception => {
                console.error(exception);
            });*/
};

const fetchResultJSON = (data, setResultJSON) => {
    if (data?.id) {
        ActionResultModel.get(data.id, 'json').then(response => {
            setResultJSON(response.data?.json);
        });
    } else {
        setResultJSON('No Results');
    }
};

const infoTooltip = (data, username, resourceName) => {
    let timestamp = parseFloat(data.started_on);
    let info = [
        {
            key: 'Execution Date',
            value: formatAsUserShortDateTime(timestamp)
        },
        { key: 'Resource', value: resourceName },
        { key: 'Action', value: data.action_name }
    ];
    if (data.hasOwnProperty('user_id') && username)
        info.push({ key: 'User', value: username ?? '-' });
    if (
        data?.act_conf &&
        (data.act_conf?.opt_fields || data.act_conf?.required_fields)
    ) {
        //FM merge opt_field with required_fields
        let fields = [
            ...(data.act_conf.opt_fields || []),
            ...(data.act_conf.required_fields || [])
        ];
        fields.map(element => {
            if (data?.params) {
                const value =
                    data.params[element['id'].replace(/^[^_]*_/, '')] || '-';
                if (element['data-label'] ?? element.label)
                    info.push({
                        key: element['data-label'] ?? element.label,
                        value: stripHtml(value)
                    });
            } else if (element.hasOwnProperty('label')) {
                info.push({
                    key: element.label,
                    value: stripHtml(element.value)
                });
            }
        });
    }
    return <InfoTooltip info={info} />;
};

//RN: SHARED SMALL COMPONENTS - ActionResult.jsx && SliderActionResult.jsx
const FormatTitle = ({ title, name, isFilter }) => {
    let headerTitle, resource;
    if (isFilter) {
        headerTitle = <Translate message="Filter" />;
        resource = name;
    } else {
        headerTitle = title || name;
        resource = name;
    }
    return (
        <>
            <span className="header-title">{headerTitle}</span>
            {resource && (
                <span className="header-resource">{` [${resource}] `}</span>
            )}
        </>
    );
};

FormatTitle.propTypes = {
    title: PropTypes.string,
    name: PropTypes.string,
    isFilter: PropTypes.bool
};

FormatTitle.defaultProps = {
    title: '',
    name: '',
    isFilter: false
};

const InfoTooltip = ({ info }) => {
    if (info && info.length)
        return info.map((element, index) => {
            let value = '';
            if (typeof element.value === 'string') {
                value = element.value;
            } else if (
                element &&
                element.value &&
                element.value.hasOwnProperty('label')
            ) {
                value = element.value.label;
            } else {
                console.info('Value is not printable', element.value);
            }
            if (
                // mb: #8798 decode base64 file param value if is a file name
                value.startsWith('INCMANUPLOADFILES::') ||
                value.startsWith('INCMANRAWRESULT::')
            ) {
                let splitName = value.split('::')[2];
                value = splitName ? atob(splitName) : value;
            }
            return (
                <dl className="mb-0 w-100 d-inline-flex" key={index}>
                    <dt className="text-truncate pe-0 text-start">
                        {element.key}:
                    </dt>
                    <dd>{value}</dd>
                </dl>
            );
        });
};

InfoTooltip.propTypes = {
    info: PropTypes.array
};

InfoTooltip.defaultProps = {
    info: []
};

const HeaderTitle = ({ collapsible, children, targetId }) => {
    if (collapsible) {
        return (
            <Collapse.Toggle targetId={targetId}>
                <Button variant="text" color="primary" className="title">
                    {children}
                </Button>
            </Collapse.Toggle>
        );
    }
    return <span className="title">{children}</span>;
};

HeaderTitle.propTypes = {
    children: PropTypes.node,
    collapsible: PropTypes.bool,
    targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const CollapseIcon = ({ targetId, isCollapsed }) => {
    const iconType = isCollapsed ? 'chevron-down' : 'chevron-up';
    return (
        <Collapse.Toggle targetId={targetId}>
            <div className="collapse-icon">
                <Icon type={iconType} />
            </div>
        </Collapse.Toggle>
    );
};

CollapseIcon.propTypes = {
    targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isCollapsed: PropTypes.bool
};

export {
    getStatusIcon,
    getStatusColor,
    FormatTitle,
    getUserDetails,
    fetchResultJSON,
    infoTooltip,
    InfoTooltip,
    HeaderTitle,
    CollapseIcon
};
